import { useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import {
  DataTable,
  DataTableExpandedRows,
  DataTablePageEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import * as api from "../../utils/Api.ts";
import type { EmailLogFiltros } from "../../utils/Api.ts";
import { showMsg } from "../../utils/Constants.tsx";
import ChartCard from "../../components/widgets/graphs/ChartCard.tsx";
import KpiCard from "../../components/widgets/graphs/KpiCard.tsx";
import {
  formatNumber,
  formatPercent,
} from "../../components/widgets/graphs/chartTheme.ts";
import { Count, FilterLink } from "../../components/reportes/ReportesCells.tsx";
import {
  clean,
  errorMessage,
  exportSheets,
  formatIsoDate,
  toIsoDate,
  useReporte,
  useScrollbarGutter,
} from "../../components/reportes/reportesUtils.ts";
import "../finanzas/dashboard.css";
import "../../components/reportes/reportes.css";
import "./email-tracking.css";

/* ------------------------------------------------------------------ */
/* Types (GET /notifications/email-log/ordenes-emitidas)                */
/* ------------------------------------------------------------------ */

type EstadoEmail =
  | "confirmado"
  | "abierto"
  | "enviado"
  | "rebotado"
  | "rechazado"
  | "error"
  | "sin_envio";

interface DestinatarioEmail {
  destinatario: string;
  estado: EstadoEmail;
  intentos: number;
  primerEnvio: string | null;
  ultimoEnvio: string | null;
  dtApertura: string | null;
  dtConfirmacion: string | null;
  estadoUltimoEnvio: EstadoEmail | null;
  dtEstadoUltimoEnvio: string | null;
  template: string | null;
  respuestaSmtp: string | null;
  error: string | null;
}

interface OrdenEmitidaEmail {
  idOrdenCompra: number;
  fechaEmision: string | null;
  idProveedor: number;
  proveedor: string | null;
  idEstadoOrden: number;
  estadoOrden: string | null;
  estadoEmail: EstadoEmail;
  destinatarios: number;
  fallidos: number;
  primerEnvio: string | null;
  ultimoEnvio: string | null;
  detalle: DestinatarioEmail[];
}

interface ResumenEmail {
  ordenes: {
    total: number;
    conEnvio: number;
    sinEnvio: number;
    conFallos: number;
    porEstado: Record<string, number>;
  };
  destinatarios: {
    total: number;
    entregados: number;
    porEstado: Record<string, number>;
    tasaEntrega: number;
    tasaApertura: number;
    tasaConfirmacion: number;
    tasaRebote: number;
    tasaRechazo: number;
    tasaError: number;
  };
}

interface OrdenesEmitidasResponse {
  resumen: ResumenEmail;
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  data: OrdenEmitidaEmail[];
}

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

// Day the API started writing email_log. OCs emitted before it have no log
// rows and would all show as "Sin envío", so it is the default "desde".
// UPDATE TO THE REAL PRODUCTION DEPLOY DATE OF THE API.
const INICIO_REGISTRO_CORREOS = "2026-11-03";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
// Export walks every page of the filters; the API caps limit at 200
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_PAGES = 50;

const ESTADOS: Record<
  EstadoEmail,
  {
    label: string;
    severity: "success" | "info" | "secondary" | "warning" | "danger";
    hint: string;
  }
> = {
  confirmado: {
    label: "Confirmado",
    severity: "success",
    hint: "El proveedor presionó «Confirmar recepción»",
  },
  abierto: {
    label: "Abierto",
    severity: "info",
    hint: "Se cargó la imagen de seguimiento (señal aproximada)",
  },
  enviado: {
    label: "Enviado",
    severity: "secondary",
    hint: "El servidor de correo aceptó el mensaje",
  },
  rebotado: {
    label: "Rebotado",
    severity: "danger",
    hint: "El correo fue devuelto por el servidor del destinatario",
  },
  rechazado: {
    label: "Rechazado",
    severity: "danger",
    hint: "El servidor de correo rechazó la dirección",
  },
  error: {
    label: "Error",
    severity: "danger",
    hint: "Falló el envío o la descarga del PDF de la OC",
  },
  sin_envio: {
    label: "Sin envío",
    severity: "warning",
    hint: "No hay registro del correo de emisión a un destinatario externo",
  },
};

const ESTADO_OPTIONS = (Object.keys(ESTADOS) as EstadoEmail[]).map((e) => ({
  label: ESTADOS[e].label,
  value: e,
}));

interface FiltrosEmail {
  fechaDesde: Date | null;
  fechaHasta: Date | null;
  nroOrdenCompra: number | null;
  idProveedor: number | null;
  proveedorNombre: string;
  estadoEmail: EstadoEmail | null;
}

const fromIsoDate = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const defaultFiltros = (): FiltrosEmail => ({
  fechaDesde: fromIsoDate(INICIO_REGISTRO_CORREOS),
  fechaHasta: new Date(),
  nroOrdenCompra: null,
  idProveedor: null,
  proveedorNombre: "",
  estadoEmail: null,
});

const toQuery = (f: FiltrosEmail): EmailLogFiltros => ({
  fechaDesde: toIsoDate(f.fechaDesde),
  fechaHasta: toIsoDate(f.fechaHasta),
  nroOrdenCompra: f.nroOrdenCompra ?? undefined,
  idProveedor: f.idProveedor ?? undefined,
  estadoEmail: f.estadoEmail ?? undefined,
});

// Every date of this screen (ordenes_emitidas.dt_cre and the email_log
// columns) is written with GETDATE(), the Chilean clock of the SQL Server.
// The driver returns them as if they were UTC ("...Z"), so they are shown
// as UTC: that prints exactly what the database has, with no hour shift
// (and no fixed offset that would break on the daylight saving changes).
const formatFechaHora = (value: string | null | undefined): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
};

const EstadoTag = ({ estado }: { estado: EstadoEmail | null | undefined }) => {
  const info = estado ? ESTADOS[estado] : undefined;
  if (!info) return <span>{estado || "-"}</span>;
  return <Tag value={info.label} severity={info.severity} title={info.hint} />;
};

/* ------------------------------------------------------------------ */
/* Screen                                                               */
/* ------------------------------------------------------------------ */

/**
 * Emitted purchase orders and the delivery status of the email sent to the
 * supplier (email_log). Only external recipients count: internal copies
 * (@salmonesdechile.com) are left out by the API.
 */
export default function SeguimientoCorreosOrdenes() {
  useScrollbarGutter();
  const toast = useRef<Toast>(null);

  const [form, setForm] = useState<FiltrosEmail>(defaultFiltros);
  const [applied, setApplied] = useState<FiltrosEmail>(defaultFiltros);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_OPTIONS[1]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);
  const [exporting, setExporting] = useState(false);

  const query = useMemo(() => toQuery(applied), [applied]);
  const queryKey = JSON.stringify(query);
  const isDirty = JSON.stringify(toQuery(form)) !== queryKey;

  const reporte = useReporte<OrdenesEmitidasResponse>(
    () => api.GET_EMAIL_LOG.getOrdenesEmitidas({ ...query, page, limit }),
    `${queryKey}|${page}|${limit}|${refreshKey}`,
  );
  const resumen = reporte.data?.resumen;
  const ordenes = reporte.data?.data ?? [];

  // Every filter change goes back to the first page
  const applyFiltros = (next: FiltrosEmail) => {
    if (
      next.fechaDesde &&
      next.fechaHasta &&
      next.fechaDesde > next.fechaHasta
    ) {
      showMsg(
        toast,
        "warn",
        "Filtros",
        "La fecha desde no puede ser mayor que la fecha hasta",
      );
      return;
    }
    setForm(next);
    setApplied(next);
    setPage(1);
    setExpandedRows(undefined);
  };
  const patchBoth = (patch: Partial<FiltrosEmail>) =>
    applyFiltros({ ...applied, ...patch });

  const onPage = (e: DataTablePageEvent) => {
    setLimit(e.rows);
    setPage(Math.floor(e.first / e.rows) + 1);
    setExpandedRows(undefined);
  };

  const reload = () => setRefreshKey((k) => k + 1);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const todas: OrdenEmitidaEmail[] = [];
      let truncado = false;
      for (let p = 1; ; p++) {
        if (p > EXPORT_MAX_PAGES) {
          truncado = true;
          break;
        }
        const res: OrdenesEmitidasResponse =
          await api.GET_EMAIL_LOG.getOrdenesEmitidas({
            ...query,
            page: p,
            limit: EXPORT_PAGE_SIZE,
          });
        todas.push(...(res?.data ?? []));
        if (!res?.hasMore) break;
      }

      const ordenesRows = todas.map((o) => ({
        "N° OC": o.idOrdenCompra,
        "Fecha emisión": formatFechaHora(o.fechaEmision),
        Proveedor: clean(o.proveedor),
        "Estado OC": clean(o.estadoOrden),
        "Estado correo": ESTADOS[o.estadoEmail]?.label ?? o.estadoEmail,
        Destinatarios: o.destinatarios,
        Fallidos: o.fallidos,
        "Primer envío": formatFechaHora(o.primerEnvio),
        "Último envío": formatFechaHora(o.ultimoEnvio),
      }));
      const destinatariosRows = todas.flatMap((o) =>
        o.detalle.map((d) => ({
          "N° OC": o.idOrdenCompra,
          Proveedor: clean(o.proveedor),
          Destinatario: d.destinatario,
          Estado: ESTADOS[d.estado]?.label ?? d.estado,
          Intentos: d.intentos,
          "Primer envío": formatFechaHora(d.primerEnvio),
          "Último envío": formatFechaHora(d.ultimoEnvio),
          Apertura: formatFechaHora(d.dtApertura),
          Confirmación: formatFechaHora(d.dtConfirmacion),
          "Respuesta SMTP": d.respuestaSmtp ?? "",
          Error: d.error ?? "",
        })),
      );
      const filtrosRows = [
        { Filtro: "Desde", Valor: formatIsoDate(query.fechaDesde) },
        { Filtro: "Hasta", Valor: formatIsoDate(query.fechaHasta) },
        { Filtro: "N° OC", Valor: query.nroOrdenCompra ?? "Todas" },
        {
          Filtro: "Proveedor",
          Valor: applied.proveedorNombre || query.idProveedor || "Todos",
        },
        {
          Filtro: "Estado correo",
          Valor: query.estadoEmail
            ? ESTADOS[query.estadoEmail as EstadoEmail]?.label
            : "Todos",
        },
      ];

      await exportSheets("seguimiento_correos_oc", [
        { name: "Órdenes", rows: ordenesRows },
        { name: "Destinatarios", rows: destinatariosRows },
        { name: "Filtros", rows: filtrosRows },
      ]);
      if (truncado) {
        showMsg(
          toast,
          "warn",
          "Exportar",
          `Se exportaron las primeras ${formatNumber(todas.length)} OC. Acota los filtros para exportar el resto.`,
        );
      }
    } catch (e) {
      console.error("Error exportando seguimiento de correos:", e);
      showMsg(toast, "error", "Exportar", errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const destinos = resumen?.destinatarios;
  const fallidosDestinos =
    (destinos?.porEstado.rechazado ?? 0) +
    (destinos?.porEstado.error ?? 0) +
    (destinos?.porEstado.rebotado ?? 0);

  return (
    <div className="fd-dashboard rp-dashboard">
      <Toast ref={toast} />

      {/* ------------------------- HEADER ------------------------- */}
      <header className="fd-hero">
        <div className="fd-hero-main">
          <div className="fd-hero-icon" aria-hidden="true">
            <i className="pi pi-envelope" />
          </div>
          <div className="fd-hero-text">
            <span className="fd-eyebrow">Compras · Órdenes de compra</span>
            <h4 className="fd-title">Seguimiento de correos de OC emitidas</h4>
            <div className="fd-meta">
              <span className="fd-meta-item">
                <i className="pi pi-calendar" />
                {formatIsoDate(query.fechaDesde)}
                <i className="pi pi-arrow-right fd-meta-arrow" />
                {formatIsoDate(query.fechaHasta)}
              </span>
              <span className="fd-meta-item">
                <i className="pi pi-clock" />
                Por fecha de emisión
              </span>
              <span
                className="fd-meta-item"
                title="Las OC emitidas antes de esta fecha no tienen registro de correo"
              >
                <i className="pi pi-info-circle" />
                Registro desde {formatIsoDate(INICIO_REGISTRO_CORREOS)}
              </span>
            </div>
          </div>
        </div>
        <div className="fd-hero-actions">
          <Button
            type="button"
            icon="pi pi-refresh"
            label="Actualizar"
            className="fd-btn fd-btn-ghost"
            loading={reporte.loading}
            onClick={reload}
          />
          <Button
            type="button"
            icon="pi pi-file-excel"
            label="Exportar Excel"
            className="fd-btn fd-btn-excel"
            loading={exporting}
            disabled={reporte.loading || !reporte.data?.total}
            onClick={exportExcel}
          />
        </div>
      </header>

      {/* ------------------------- FILTROS ------------------------- */}
      <section className="fd-filters" aria-label="Filtros">
        <div className="fd-filters-head">
          <span className="fd-filters-title">
            <i className="pi pi-sliders-h" />
            Filtros
          </span>
          {isDirty ? (
            <span className="fd-dirty">
              <span className="fd-dirty-dot" />
              Cambios sin aplicar
            </span>
          ) : null}
        </div>
        <div className="fd-filter-grid">
          <div className="fd-field">
            <label htmlFor="ec-desde">Desde</label>
            <Calendar
              inputId="ec-desde"
              value={form.fechaDesde}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaDesde: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              maxDate={form.fechaHasta ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="ec-hasta">Hasta</label>
            <Calendar
              inputId="ec-hasta"
              value={form.fechaHasta}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  fechaHasta: (e.value as Date) ?? null,
                }))
              }
              dateFormat="dd/mm/yy"
              placeholder="dd/mm/aaaa"
              showIcon
              showButtonBar
              minDate={form.fechaDesde ?? undefined}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="ec-nro-oc">N° OC</label>
            <InputNumber
              inputId="ec-nro-oc"
              value={form.nroOrdenCompra}
              placeholder="Todas"
              useGrouping={false}
              min={1}
              onValueChange={(e) =>
                setForm((f) => ({ ...f, nroOrdenCompra: e.value ?? null }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFiltros(form);
              }}
            />
          </div>
          <div className="fd-field">
            <label htmlFor="ec-estado">Estado del correo</label>
            <Dropdown
              inputId="ec-estado"
              value={form.estadoEmail}
              options={ESTADO_OPTIONS}
              placeholder="Todos"
              showClear
              onChange={(e) =>
                setForm((f) => ({ ...f, estadoEmail: e.value ?? null }))
              }
            />
          </div>
        </div>
        <div className="fd-filters-foot">
          <div className="fd-note ec-filters-note">
            Para filtrar por proveedor, haz clic en su nombre en la tabla.
          </div>
          <div className="fd-filter-actions">
            {applied.idProveedor ? (
              <span className="fd-chip">
                <i className="pi pi-building" />
                <span className="fd-chip-text">
                  {applied.proveedorNombre ||
                    `Proveedor ${applied.idProveedor}`}
                </span>
                <button
                  type="button"
                  className="fd-chip-close"
                  title="Quitar filtro de proveedor"
                  aria-label="Quitar filtro de proveedor"
                  onClick={() =>
                    patchBoth({ idProveedor: null, proveedorNombre: "" })
                  }
                >
                  <i className="pi pi-times" />
                </button>
              </span>
            ) : null}
            <Button
              type="button"
              label="Limpiar"
              icon="pi pi-filter-slash"
              className="fd-btn fd-btn-subtle"
              onClick={() => applyFiltros(defaultFiltros())}
            />
            <Button
              type="button"
              label="Aplicar filtros"
              icon="pi pi-check"
              className={`fd-btn fd-btn-primary${isDirty ? " is-dirty" : ""}`}
              onClick={() => applyFiltros(form)}
            />
          </div>
        </div>
      </section>

      {/* ------------------------- KPIs ------------------------- */}
      {reporte.error ? (
        <ChartCard
          title="Resumen"
          error={reporte.error}
          onRetry={reporte.reload}
          height={80}
        />
      ) : (
        <>
          <div className="fd-kpis">
            <KpiCard
              title="OC emitidas"
              value={formatNumber(resumen?.ordenes.total)}
              icon="pi pi-shopping-cart"
              hint="En el rango de fechas"
              loading={reporte.loading && !resumen}
            />
            <KpiCard
              title="OC con correo enviado"
              value={formatNumber(resumen?.ordenes.conEnvio)}
              icon="pi pi-send"
              tone="success"
              hint="Al menos un destinatario externo registrado"
              progress={
                resumen?.ordenes.total
                  ? (resumen.ordenes.conEnvio / resumen.ordenes.total) * 100
                  : 0
              }
              loading={reporte.loading && !resumen}
            />
            <KpiCard
              title="OC sin envío"
              value={formatNumber(resumen?.ordenes.sinEnvio)}
              icon="pi pi-inbox"
              tone={resumen?.ordenes.sinEnvio ? "warning" : "default"}
              hint="Clic para ver solo estas OC"
              loading={reporte.loading && !resumen}
              onClick={() => patchBoth({ estadoEmail: "sin_envio" })}
            />
            <KpiCard
              title="OC con fallos"
              value={formatNumber(resumen?.ordenes.conFallos)}
              icon="pi pi-exclamation-triangle"
              tone={resumen?.ordenes.conFallos ? "danger" : "default"}
              hint="Algún destinatario externo rechazado, con error o rebotado"
              loading={reporte.loading && !resumen}
            />
          </div>
          <div className="fd-kpis">
            <KpiCard
              title="Tasa de entrega"
              value={formatPercent(destinos?.tasaEntrega)}
              icon="pi pi-check-circle"
              tone="success"
              progress={destinos?.tasaEntrega}
              hint={`${formatNumber(destinos?.entregados)} de ${formatNumber(destinos?.total)} destinatarios aceptados por el servidor`}
              loading={reporte.loading && !resumen}
            />
            <KpiCard
              title="Tasa de apertura"
              value={formatPercent(destinos?.tasaApertura)}
              icon="pi pi-eye"
              progress={destinos?.tasaApertura}
              hint="Aproximada: Outlook bloquea imágenes por defecto"
              loading={reporte.loading && !resumen}
            />
            <KpiCard
              title="Tasa de confirmación"
              value={formatPercent(destinos?.tasaConfirmacion)}
              icon="pi pi-verified"
              tone="success"
              progress={destinos?.tasaConfirmacion}
              hint="Proveedores que confirmaron la recepción"
              loading={reporte.loading && !resumen}
            />
            <KpiCard
              title="Destinatarios con fallo"
              value={formatNumber(fallidosDestinos)}
              icon="pi pi-times-circle"
              tone={fallidosDestinos ? "danger" : "default"}
              hint={`Rechazo ${formatPercent(destinos?.tasaRechazo)} · Error ${formatPercent(destinos?.tasaError)} · Rebote ${formatPercent(destinos?.tasaRebote)}`}
              loading={reporte.loading && !resumen}
            />
          </div>
        </>
      )}

      {/* ------------------------- TABLA ------------------------- */}
      <ChartCard
        title="Órdenes emitidas"
        subtitle="Estado del correo al proveedor · despliega una fila para ver cada destinatario"
      >
        <div className="fd-note">
          «Enviado» indica que el servidor de correo aceptó el mensaje, no que
          llegó a la bandeja. Los rebotes todavía no se registran, por lo que un
          correo rebotado se muestra como enviado.
        </div>
        <div className="fd-note">
          «Abierto» es aproximado: puede quedar vacío si se bloquean las
          imágenes, y un filtro de seguridad de correo puede activarlo sin que
          nadie lea el mensaje. «Confirmado» es la señal confiable, porque una
          persona presionó el botón. Los correos enviados antes de activar el
          seguimiento quedan como «Enviado».
        </div>
        <DataTable
          value={reporte.error ? [] : ordenes}
          dataKey="idOrdenCompra"
          loading={reporte.loading}
          lazy
          paginator
          first={(page - 1) * limit}
          rows={limit}
          totalRecords={reporte.data?.total ?? 0}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPage={onPage}
          expandedRows={expandedRows}
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowExpansionTemplate={(o: OrdenEmitidaEmail) => (
            <DetalleDestinatarios orden={o} />
          )}
          size="small"
          stripedRows
          emptyMessage="No hay OC emitidas para los filtros seleccionados"
          className="fd-table"
        >
          <Column expander style={{ width: "3rem" }} />
          <Column header="N° OC" field="idOrdenCompra" bodyClassName="fd-num" />
          <Column
            header="Emisión"
            body={(o: OrdenEmitidaEmail) => formatFechaHora(o.fechaEmision)}
            bodyClassName="fd-num"
          />
          <Column
            header="Proveedor"
            body={(o: OrdenEmitidaEmail) => (
              <FilterLink
                name={o.proveedor ?? ""}
                title="Filtrar por este proveedor"
                onClick={() =>
                  patchBoth({
                    idProveedor: o.idProveedor,
                    proveedorNombre: clean(o.proveedor),
                  })
                }
              />
            )}
          />
          <Column
            header="Estado OC"
            body={(o: OrdenEmitidaEmail) => clean(o.estadoOrden) || "-"}
          />
          <Column
            header="Correo"
            body={(o: OrdenEmitidaEmail) => (
              <EstadoTag estado={o.estadoEmail} />
            )}
          />
          <Column
            header="Destinatarios"
            body={(o: OrdenEmitidaEmail) => (
              <span className="fd-num">
                <Count value={o.destinatarios} />
                {o.fallidos ? (
                  <span className="ec-fallidos">
                    {" "}
                    · {formatNumber(o.fallidos)} con fallo
                  </span>
                ) : null}
              </span>
            )}
          />
          <Column
            header="Último envío"
            body={(o: OrdenEmitidaEmail) => formatFechaHora(o.ultimoEnvio)}
            bodyClassName="fd-num"
          />
        </DataTable>
      </ChartCard>
    </div>
  );
}

function DetalleDestinatarios({ orden }: { orden: OrdenEmitidaEmail }) {
  if (!orden.detalle.length) {
    return (
      <div className="ec-detalle-vacio">
        <i className="pi pi-inbox" /> No hay correos registrados a destinatarios
        externos para la OC N° {orden.idOrdenCompra}.
      </div>
    );
  }
  return (
    <div className="ec-detalle">
      <DataTable
        value={orden.detalle}
        dataKey="destinatario"
        size="small"
        className="fd-table"
      >
        <Column header="Destinatario" field="destinatario" />
        <Column
          header="Estado"
          body={(d: DestinatarioEmail) => <EstadoTag estado={d.estado} />}
        />
        <Column
          header="Intentos"
          body={(d: DestinatarioEmail) => <Count value={d.intentos} />}
        />
        <Column
          header="Primer envío"
          body={(d: DestinatarioEmail) => formatFechaHora(d.primerEnvio)}
          bodyClassName="fd-num"
        />
        <Column
          header="Último envío"
          body={(d: DestinatarioEmail) => (
            <span className="fd-num">
              {formatFechaHora(d.ultimoEnvio)}
              {d.estadoUltimoEnvio && d.estadoUltimoEnvio !== d.estado ? (
                <span className="ec-sub">
                  {" "}
                  ({ESTADOS[d.estadoUltimoEnvio]?.label ?? d.estadoUltimoEnvio})
                </span>
              ) : null}
            </span>
          )}
        />
        <Column
          header="Apertura"
          body={(d: DestinatarioEmail) => formatFechaHora(d.dtApertura)}
          bodyClassName="fd-num"
        />
        <Column
          header="Confirmación"
          body={(d: DestinatarioEmail) => formatFechaHora(d.dtConfirmacion)}
          bodyClassName="fd-num"
        />
        <Column
          header="Respuesta servidor"
          body={(d: DestinatarioEmail) => {
            const texto = d.error || d.respuestaSmtp;
            return texto ? (
              <span
                className={`ec-smtp${d.error ? " ec-smtp-error" : ""}`}
                title={texto}
              >
                {texto}
              </span>
            ) : (
              "-"
            );
          }}
        />
      </DataTable>
    </div>
  );
}
